import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useTasks } from './TasksContext';
import { useCompany } from './CompanyContext';
import { sanitizeForFirestore } from '../utils/firestoreUtils';
import PlaybookEngine from '../playbooks/PlaybookEngine';

const AutomationContext = createContext();

export const useAutomation = () => {
  const ctx = useContext(AutomationContext);
  if (!ctx) throw new Error('useAutomation must be used within an AutomationProvider');
  return ctx;
};

// ─── API helpers ──────────────────────────────────────────────────────────────

async function _apiFetch(path, method = 'GET', body = null) {
  const token = await auth.currentUser?.getIdToken(false);
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || data.message || `Request failed (${res.status})`);
  return data;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export const AutomationProvider = ({ children }) => {
  const { companyId } = useCompany();
  const { addTask } = useTasks();

  // ── Legacy automations (Firestore direct, client-side execution)
  const [automations, setAutomations] = useState([]);

  // ── New backend playbooks (Firestore listener for real-time updates)
  const [playbooks, setPlaybooks] = useState([]);

  const [loading, setLoading] = useState(true);

  // Subscribe to legacy automations
  useEffect(() => {
    if (!db || !companyId) return;
    const ref = collection(db, 'companies', companyId, 'automations');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setAutomations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('[AutomationContext] automations listener error:', err);
        setAutomations([]);
        setLoading(false);
      }
    );
    return unsub;
  }, [companyId]);

  // Subscribe to new backend playbooks (stored in Firestore via backend API)
  useEffect(() => {
    if (!db || !companyId) return;
    const ref = collection(db, 'companies', companyId, 'playbooks');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Sort by createdAt descending
        docs.sort((a, b) => {
          const ta = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const tb = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return tb - ta;
        });
        setPlaybooks(docs);
      },
      (err) => {
        console.error('[AutomationContext] playbooks listener error:', err);
        setPlaybooks([]);
      }
    );
    return unsub;
  }, [companyId]);

  // ── Legacy automation CRUD (direct Firestore) ─────────────────────────────

  const addAutomation = async (automation) => {
    if (!db || !companyId) throw new Error('Firestore not initialized');
    const ref = collection(db, 'companies', companyId, 'automations');
    const payload = sanitizeForFirestore({
      ...automation,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    const docRef = await addDoc(ref, payload);
    return docRef.id;
  };

  const updateAutomation = async (id, updates) => {
    if (!db || !companyId) throw new Error('Firestore not initialized');
    const ref = doc(db, 'companies', companyId, 'automations', id);
    await updateDoc(ref, sanitizeForFirestore({ ...updates, updatedAt: serverTimestamp() }));
  };

  const deleteAutomation = async (id) => {
    if (!db || !companyId) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, 'companies', companyId, 'automations', id));
  };

  const duplicateAutomation = async (id) => {
    const original = automations.find((a) => a.id === id);
    if (!original) throw new Error('Playbook not found');
    const { id: _id, createdAt: _c, updatedAt: _u, ...rest } = original;
    return addAutomation({ ...rest, name: `${original.name} (Copy)`, active: false });
  };

  // ── New backend playbook CRUD (API-backed) ────────────────────────────────

  const addPlaybook = async (payload) => {
    const data = await _apiFetch('/api/playbooks', 'POST', payload);
    return data.playbook?.id;
  };

  const updatePlaybook = async (id, payload) => {
    await _apiFetch(`/api/playbooks/${id}`, 'PUT', payload);
  };

  const patchPlaybook = async (id, updates) => {
    await _apiFetch(`/api/playbooks/${id}`, 'PATCH', updates);
  };

  const deletePlaybook = async (id) => {
    await _apiFetch(`/api/playbooks/${id}`, 'DELETE');
  };

  const duplicatePlaybook = async (id) => {
    const original = playbooks.find((p) => p.id === id);
    if (!original) throw new Error('Playbook not found');
    const { id: _id, createdAt: _c, updatedAt: _u, companyId: _cid, createdBy: _cb, ...rest } = original;
    return addPlaybook({ ...rest, name: `${original.name} (Copy)`, isActive: false });
  };

  // ── Client-side legacy execution (leadCreated, stageChanged) ─────────────

  const runAutomationsForEvent = async (eventType, payload) => {
    await PlaybookEngine.run(eventType, payload, automations, { companyId, addTask });
  };

  const value = {
    // Legacy data
    automations,
    loading,

    // New playbooks data
    playbooks,

    // Legacy CRUD
    addAutomation,
    updateAutomation,
    deleteAutomation,
    duplicateAutomation,

    // New backend playbook CRUD
    addPlaybook,
    updatePlaybook,
    patchPlaybook,
    deletePlaybook,
    duplicatePlaybook,

    // Execution
    runAutomationsForEvent,
    runPlaybooksForEvent: runAutomationsForEvent,
  };

  return <AutomationContext.Provider value={value}>{children}</AutomationContext.Provider>;
};

export default AutomationContext;
